<template>
  <v-dialog :value="value" v-on:input="$emit('input', $event)" max-width="550px" :persistent="saving">
    <v-form ref="form" v-model="valid">
      <v-card>
        <v-card-title>
          <span v-if="isNew" class="headline">Add letter</span>
          <span v-if="!isNew" class="headline">{{ model.uppercase }} {{ model.lowercase }}</span>
        </v-card-title>
        <v-card-text>
          <v-alert :value="showErrorOnSave" type="error">{{ errorOnSave }}</v-alert>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs6>
                <v-text-field
                        v-model.trim="edited.uppercase"
                        required
                        :rules="[required]"
                        label="Upper case"
                        :error-messages="errorMessages"
                        :disabled="saving"
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                        v-model.trim="edited.lowercase"
                        required
                        :rules="[required]"
                        label="Lower case"
                        :error-messages="errorMessages"
                        :disabled="saving"
                ></v-text-field>
              </v-flex>

              <v-flex xs12>
                <v-autocomplete
                  v-model="edited.examples"
                  :loading="examplesLoading"
                  :items="exampleItems"
                  :search-input.sync="examplesSearch"
                  cache-items
                  flat
                  chips
                  hide-no-data
                  hide-details
                  return-object
                  multiple
                  label="Examples"
                ></v-autocomplete>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click.native="close">Cancel</v-btn>
          <v-btn color="blue darken-1" flat @click.native="save" :loading="saving">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { LettersApi, WordsApi } from '../common/api'
import { ADD_LETTER, UPDATE_LETTER } from '../store/mutation-types'
import { mapGetters } from 'vuex'
import latest from 'promise-latest'
import debounce from 'lodash/debounce'

const queryWords = latest(WordsApi.query)

const COMMON_ERROR = 'Error has occurred while saving'

export default {
  props: ['value', 'model'],
  data () {
    return {
      valid: false,
      saving: false,
      errorMessages: [],
      errorOnSave: COMMON_ERROR,
      showErrorOnSave: false,
      examplesLoading: false,
      examplesSearch: null,
      exampleItems: [],
      edited: this.defaultModel()
    }
  },
  computed: {
    isNew () {
      return !this.model
    },
    ...mapGetters([
      'sourceLanguageId'
    ])
  },
  watch: {
    value () {
      this.showErrorOnSave = false
      this.$refs.form.resetValidation()
      if (!this.value) {
        return
      }
      if (this.isNew) {
        this.edited = this.defaultModel()
      } else {
        this.edited = { ...this.model }
        this.exampleItems = this.model.examples
      }
    },
    examplesSearch (val) {
      val && this.debouncedQuerySelections(val)
    }
  },
  created () {
    this.debouncedQuerySelections = debounce(this.querySelections, 200)
  },
  methods: {
    defaultModel () {
      return {
        uppercase: null,
        lowercase: null,
        languageId: this.sourceLanguageId,
        examples: []
      }
    },
    validate () {
      this.errorOnSave = COMMON_ERROR
      return this.$refs.form.validate()
    },
    close () {
      if (this.saving) {
        return
      }
      this.$emit('input', false)
    },
    async save () {
      if (this.saving) {
        return
      }
      if (!this.validate()) {
        return
      }
      this.showErrorOnSave = false
      this.saving = true
      try {
        if (this.isNew) {
          const { data } = await LettersApi.create(this.getEditedToSave())
          this.$store.commit(ADD_LETTER, data)
        } else {
          const { data } = await LettersApi.update(this.getEditedToSave())
          this.$store.commit(UPDATE_LETTER, data)
        }
        this.saving = false
        this.close()
      } catch (e) {
        this.saving = false
        this.showErrorOnSave = true
        console.error(e)
      }
    },
    getEditedToSave () {
      const obj = {
        ...this.edited,
        exampleIds: this.edited.examples.map(v => v.id)
      }
      delete obj.examples
      return obj
    },
    async querySelections (v) {
      this.examplesLoading = true

      try {
        const { data } = await queryWords({
          sourceDictionaryId: this.sourceLanguageId,
          search: v
        })

        this.exampleItems = data.items
      } catch (e) {
        alert(e)
      } finally {
        this.examplesLoading = false
      }
    },
    required (value) {
      return !!value || 'Required.'
    }
  }
}
</script>
